import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BsGithub } from "react-icons/bs";
import { FaLinkedinIn } from "react-icons/fa";
import { FiArrowRight, FiArrowUpRight, FiSearch } from "react-icons/fi";
import { IoCompass, IoMail } from "react-icons/io5";
import { SiX } from "react-icons/si";
import { useClock } from "@/hooks/useClock";
import { useMatchMedia } from "@/hooks/useMatchMedia";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useScrollLock } from "@/hooks/useScrollLock";
import { getLatestPosts } from "@/utils/blog";
import { getSocialMediaLink } from "@/utils/external-links";
import { BatteryGlyph, ControlCentreGlyph, ScaledStage, WifiGlyph } from "@/components/device/DeviceParts";
import { SectionWindowContent } from "@/components/device/SectionWindowContent";
import { useGlassLight } from "@/components/device/useGlassLight";
import {
    DEVICE_SECTIONS,
    countRecentPosts,
    isDeviceSectionId,
    type DeviceSectionId,
} from "@/components/device/sections";
import "@/styles/device-os.css";

type Variant = "mac" | "ipad-landscape" | "ipad-portrait";

/** Logical screen size each device is designed at; `ScaledStage` fits it to the viewport. */
const SCREEN: Record<Variant, { width: number; height: number }> = {
    mac: { width: 1088, height: 680 },
    "ipad-landscape": { width: 880, height: 600 },
    "ipad-portrait": { width: 640, height: 880 },
};

/** Lid swing, then the start-up screen, then the desktop. */
const LID_S = 1;
const BOOT_MS: Record<"mac" | "ipad", number> = { mac: 2700, ipad: 2100 };
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export type DesktopGateProps = {
    /** Play the full lid / start-up sequence instead of a quick power-on. */
    playBoot: boolean;
    /** Leave for the classic page, optionally scrolled to a section. */
    onExit: (section: DeviceSectionId | null) => void;
};

/**
 * Tablet and desktop entry (variant A): the home page opens as a MacBook or an
 * iPad whose apps are the site's sections. Each opens as a window with a
 * section sidebar; every way out lands on the classic page underneath, which
 * stays mounted (inert) the whole time.
 */
export function DesktopGate({ playBoot, onExit }: DesktopGateProps) {
    const { t, i18n } = useTranslation();
    const { locale = "en" } = useParams<{ locale: string }>();
    const reducedMotion = usePrefersReducedMotion();
    const isMac = useMatchMedia("(min-width: 1100px)");
    const isPortrait = useMatchMedia("(orientation: portrait)");
    const variant: Variant = isMac ? "mac" : isPortrait ? "ipad-portrait" : "ipad-landscape";
    const screen = SCREEN[variant];
    const now = useClock();
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const refract = useGlassLight(dialogRef);

    const animate = !reducedMotion;
    const [stage, setStage] = useState<"boot" | "desktop">(playBoot && animate ? "boot" : "desktop");
    // Bumped on "Replay intro" so the lid and power-on animations run again.
    const [run, setRun] = useState(0);
    const [openId, setOpenId] = useState<DeviceSectionId | null>(null);
    const pushedHistory = useRef(false);

    useScrollLock(true);

    // The page underneath stays in the DOM for crawlers and the way out, but
    // takes no focus or clicks while the device is up.
    useEffect(() => {
        const root = document.getElementById("root");
        root?.setAttribute("inert", "");
        dialogRef.current?.focus();
        return () => root?.removeAttribute("inert");
    }, []);

    useEffect(() => {
        if (stage !== "boot") return;
        const id = window.setTimeout(() => setStage("desktop"), BOOT_MS[isMac ? "mac" : "ipad"]);
        return () => window.clearTimeout(id);
    }, [stage, isMac, run]);

    // Back closes the window that pushed its hash; Forward reopens it.
    useEffect(() => {
        const onPop = () => {
            const hash = window.location.hash.replace(/^#/, "");
            if (isDeviceSectionId(hash)) {
                setOpenId(hash);
            } else {
                pushedHistory.current = false;
                setOpenId(null);
            }
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const pathOnly = () => `${window.location.pathname}${window.location.search}`;

    const openWindow = useCallback(
        (id: DeviceSectionId) => {
            const url = `${pathOnly()}#${id}`;
            if (openId === null) {
                window.history.pushState(window.history.state, "", url);
                pushedHistory.current = true;
            } else {
                window.history.replaceState(window.history.state, "", url);
            }
            setOpenId(id);
        },
        [openId],
    );

    const closeWindow = useCallback(() => {
        setOpenId(null);
        if (pushedHistory.current) {
            pushedHistory.current = false;
            window.history.back();
        } else {
            window.history.replaceState(window.history.state, "", pathOnly());
        }
    }, []);

    const exit = useCallback(
        (section: DeviceSectionId | null) => {
            window.history.replaceState(window.history.state, "", section ? `${pathOnly()}#${section}` : pathOnly());
            onExit(section);
        },
        [onExit],
    );

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && openId !== null) closeWindow();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openId, closeWindow]);

    const replay = () => {
        setOpenId(null);
        if (pushedHistory.current) {
            pushedHistory.current = false;
            window.history.back();
        }
        setRun((n) => n + 1);
        if (animate) setStage("boot");
    };

    const booting = stage === "boot";
    const lidFrom = booting && animate ? -86 : 0;

    const desktop = (
        <OsDesktop
            variant={variant}
            now={now}
            language={i18n.language}
            locale={locale}
            openId={openId}
            onOpen={openWindow}
            onClose={closeWindow}
            onExit={exit}
            onReplay={replay}
        />
    );

    const screenContent = (
        <ScaledStage width={screen.width} height={screen.height} className="os-screen">
            {booting ? (
                <div className="os-boot">
                    <img className="os-boot-logo" src="/logo-white.png" alt="" width={112} height={112} decoding="async" />
                    <span className="os-boot-bar" aria-hidden="true">
                        <span />
                    </span>
                    <span className="sr-only">{t("deviceOs.gate.starting")}</span>
                </div>
            ) : (
                desktop
            )}
            {/* Black glass that fades away as the screen powers on. */}
            {animate && (
                <motion.span
                    key={`power-${run}`}
                    className="os-power"
                    aria-hidden="true"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: booting && isMac ? LID_S * 0.6 : 0 }}
                />
            )}
            <span className="device-glare" aria-hidden="true" />
        </ScaledStage>
    );

    return createPortal(
        <div
            ref={dialogRef}
            className={`os-gate is-${variant}${refract ? " lg-refract" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
        >
            <header className="os-gate-bar">
                <span id={titleId} className="os-gate-brand">
                    Nicholas Y<span className="os-gate-dot">.</span>
                    <span className="sr-only"> {t("deviceOs.gate.aria")}</span>
                </span>
                <button type="button" className="os-gate-skip" onClick={() => exit(null)}>
                    {t("deviceOs.gate.skip")}
                    <FiArrowRight aria-hidden="true" />
                </button>
            </header>

            <div className="os-gate-stage">
                {variant === "mac" ? (
                    <div className="os-mac">
                        <div className="os-mac-hinge-stage">
                            <motion.div
                                key={`lid-${run}`}
                                className="os-mac-lid"
                                initial={{ rotateX: lidFrom }}
                                animate={{ rotateX: 0 }}
                                transition={{ duration: animate ? LID_S : 0, ease: EASE_OUT }}
                            >
                                <div className="os-mac-bezel">
                                    <span className="os-mac-notch" aria-hidden="true">
                                        <span className="device-lens" />
                                        <span className="os-mac-led" />
                                    </span>
                                    {screenContent}
                                </div>
                            </motion.div>
                        </div>
                        <span className="os-mac-hinge" aria-hidden="true" />
                        <span className="os-mac-base" aria-hidden="true">
                            <span className="os-mac-scoop" />
                        </span>
                        <span className="os-mac-shadow" aria-hidden="true" />
                    </div>
                ) : (
                    <div className={`os-ipad${variant === "ipad-portrait" ? " is-portrait" : ""}`}>
                        <span className="os-ipad-btn os-ipad-btn-top" aria-hidden="true" />
                        <span className="os-ipad-btn os-ipad-btn-vol1" aria-hidden="true" />
                        <span className="os-ipad-btn os-ipad-btn-vol2" aria-hidden="true" />
                        <div className="os-ipad-frame">
                            <div className="os-ipad-bezel">
                                <span className="os-ipad-camera device-lens" aria-hidden="true" />
                                {screenContent}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <p className="os-gate-hint">{variant === "mac" ? t("deviceOs.gate.hint") : t("deviceOs.gate.hintTouch")}</p>
        </div>,
        document.body,
    );
}

type OsDesktopProps = {
    variant: Variant;
    now: Date | null;
    language: string;
    locale: string;
    openId: DeviceSectionId | null;
    onOpen: (id: DeviceSectionId) => void;
    onClose: () => void;
    onExit: (section: DeviceSectionId | null) => void;
    onReplay: () => void;
};

function OsDesktop({ variant, now, language, locale, openId, onOpen, onClose, onExit, onReplay }: OsDesktopProps) {
    const { t } = useTranslation();
    const isMac = variant === "mac";
    const posts = getLatestPosts(locale, 20);
    const latest = posts[0] ?? null;
    const recentCount = now ? countRecentPosts(posts.map((post) => post.date), now) : 0;
    const openSection = DEVICE_SECTIONS.find((section) => section.id === openId) ?? null;

    const time = now?.toLocaleTimeString(language, { hour: "numeric", minute: "2-digit" }) ?? "";
    const day = now?.toLocaleDateString(language, { weekday: "short", day: "numeric", month: "short" }) ?? "";
    const postDate = latest ? new Date(latest.date).toLocaleDateString(language, { month: "short", day: "numeric" }) : "";

    return (
        <div className={`os-desktop device-wallpaper is-${variant}`}>
            {isMac ? (
                <div className="os-menubar">
                    <span className="os-menubar-left">
                        <img className="os-menubar-mark" src="/logo-white.png" alt="" width={14} height={14} decoding="async" />
                        <strong>{openSection ? t(openSection.labelKey) : t("deviceOs.gate.finder")}</strong>
                        <span aria-hidden="true">{t("apps.menu.file")}</span>
                        <span aria-hidden="true">{t("deviceOs.gate.menu.edit")}</span>
                        <span aria-hidden="true">{t("apps.menu.view")}</span>
                        <span aria-hidden="true">{t("apps.menu.window")}</span>
                        <span aria-hidden="true">{t("deviceOs.gate.menu.help")}</span>
                    </span>
                    <span className="os-menubar-right">
                        <span className="os-menubar-status">
                            <span className="device-pulse-dot" aria-hidden="true" />
                            {t("deviceOs.gate.available")}
                        </span>
                        <button type="button" className="os-menubar-replay" onClick={onReplay}>
                            {t("deviceOs.gate.replay")}
                        </button>
                        <span className="os-menubar-icons" aria-hidden="true">
                            <BatteryGlyph />
                            <WifiGlyph />
                            <FiSearch />
                            <ControlCentreGlyph />
                        </span>
                        <span aria-hidden="true">{day}</span>
                        <span aria-hidden="true">{time}</span>
                    </span>
                </div>
            ) : (
                <div className="os-statusbar" aria-hidden="true">
                    <span>
                        {time} <span className="os-statusbar-date">{day}</span>
                    </span>
                    <span className="os-statusbar-icons">
                        <WifiGlyph />
                        <span>100%</span>
                        <BatteryGlyph level={1} />
                    </span>
                </div>
            )}

            <div className="os-widgets">
                <div className="os-widget lg-glass">
                    <span className="os-widget-kicker is-accent">
                        <span className="device-pulse-dot" aria-hidden="true" />
                        {t("profile.availableForProjects")}
                    </span>
                    <strong className="os-widget-name">Nicholas Y</strong>
                    <span className="os-widget-body">{t("deviceOs.gate.ownerBody")}</span>
                </div>
                {latest && (
                    <button type="button" className="os-widget is-button lg-glass" onClick={() => onOpen("blog")}>
                        <span className="os-widget-kicker">
                            {t("deviceOs.latestArticle")} · {postDate}
                        </span>
                        <strong className="os-widget-title">{latest.title}</strong>
                    </button>
                )}
            </div>

            {isMac && (
                <div className="os-files">
                    <button type="button" className="os-file" onClick={() => onOpen("resume")}>
                        <span className="os-file-doc" aria-hidden="true">
                            <span className="os-file-lines" />
                            <span className="os-file-tag">PDF</span>
                        </span>
                        {t("deviceOs.gate.resumeFile")}
                    </button>
                    <button type="button" className="os-file" onClick={() => onOpen("portfolio")}>
                        <span className="os-file-folder" aria-hidden="true">
                            <span />
                        </span>
                        {t("deviceOs.gate.projectsFolder")}
                    </button>
                </div>
            )}

            <nav className="os-apps" aria-label={t("deviceOs.sectionsAria")}>
                {DEVICE_SECTIONS.map((section, index) => {
                    const badge = section.id === "blog" && recentCount > 0 ? recentCount : null;
                    return (
                        <button
                            key={section.id}
                            type="button"
                            className="os-app device-pop"
                            style={{ animationDelay: `${0.05 * (index + 1)}s` }}
                            onClick={() => onOpen(section.id)}
                            aria-haspopup="dialog"
                        >
                            <span className="device-tile is-image" aria-hidden="true">
                                <img src={section.image} alt="" width={256} height={256} decoding="async" draggable={false} />
                                {badge !== null && <span className="device-badge">{badge}</span>}
                            </span>
                            {t(section.labelKey)}
                        </button>
                    );
                })}
                {/* Not a home-page section, so it leaves the desktop for its own page instead of opening a window. */}
                <Link
                    className="os-app device-pop"
                    style={{ animationDelay: `${0.05 * (DEVICE_SECTIONS.length + 1)}s` }}
                    to={`/${locale}/loop-breaker`}
                >
                    <span className="device-tile is-image" aria-hidden="true">
                        <img src="/apps/os-icons/loop-breaker.webp" alt="" width={256} height={256} decoding="async" draggable={false} />
                    </span>
                    Loop Breaker
                </Link>
            </nav>

            <nav className="os-dock lg-glass lg-glass--clear" aria-label={t("deviceOs.dockAria")}>
                <button type="button" className="os-dock-item device-tile" onClick={onClose} aria-label={t("deviceOs.gate.finder")} data-label={t("deviceOs.gate.finder")}>
                    <img className="os-dock-mark" src="/logo.png" alt="" width={50} height={50} decoding="async" />
                    <span className="os-dock-running" aria-hidden="true" />
                </button>
                <a className="os-dock-item device-tile" data-app="github" href={getSocialMediaLink("github")} target="_blank" rel="noreferrer noopener" aria-label={t("apps.dock.github")} data-label={t("apps.dock.github")}>
                    <BsGithub aria-hidden="true" />
                </a>
                <a className="os-dock-item device-tile" data-app="x" href={getSocialMediaLink("x")} target="_blank" rel="noreferrer noopener" aria-label={t("apps.dock.x")} data-label={t("apps.dock.x")}>
                    <SiX aria-hidden="true" />
                </a>
                <a className="os-dock-item device-tile" data-app="linkedin" href={getSocialMediaLink("linkedin")} target="_blank" rel="noreferrer noopener" aria-label={t("apps.dock.linkedin")} data-label={t("apps.dock.linkedin")}>
                    <FaLinkedinIn aria-hidden="true" />
                </a>
                <button type="button" className="os-dock-item device-tile" data-app="mail" onClick={() => onOpen("contact")} aria-label={t("apps.dock.email")} data-label={t("apps.dock.email")}>
                    <IoMail aria-hidden="true" />
                </button>
                <span className="os-dock-divider" aria-hidden="true" />
                <button type="button" className="os-dock-item device-tile" data-app="safari" onClick={() => onExit(null)} aria-label={t("deviceOs.gate.classic")} data-label={t("deviceOs.gate.classic")}>
                    <IoCompass aria-hidden="true" />
                </button>
            </nav>

            {!isMac && <span className="os-homebar" aria-hidden="true" />}

            <AnimatePresence>
                {openSection && (
                    <SectionWindow
                        key="window"
                        sectionId={openSection.id}
                        onSelect={onOpen}
                        onClose={onClose}
                        onOpenFull={() => onExit(openSection.id)}
                        locale={locale}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function SectionWindow({
    sectionId,
    onSelect,
    onClose,
    onOpenFull,
    locale,
}: {
    sectionId: DeviceSectionId;
    onSelect: (id: DeviceSectionId) => void;
    onClose: () => void;
    onOpenFull: () => void;
    locale: string;
}) {
    const { t } = useTranslation();
    const reducedMotion = usePrefersReducedMotion();
    const headingId = useId();
    const bodyRef = useRef<HTMLDivElement>(null);
    const section = DEVICE_SECTIONS.find((item) => item.id === sectionId) ?? DEVICE_SECTIONS[0];

    // A new section starts at the top, like switching items in a Finder sidebar.
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [sectionId]);

    return (
        <motion.section
            className="os-window"
            aria-labelledby={headingId}
            initial={reducedMotion ? false : { opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: reducedMotion ? 0.1 : 0.3, ease: [0.32, 0.72, 0, 1] }}
        >
            <div className="os-window-lights">
                <button type="button" className="os-light is-close" onClick={onClose} aria-label={t("deviceOs.gate.closeWindow")} />
                <button type="button" className="os-light is-min" onClick={onClose} aria-label={t("deviceOs.gate.minimiseWindow")} />
                <button type="button" className="os-light is-full" onClick={onOpenFull} aria-label={t("deviceOs.gate.openFull")} />
            </div>

            <nav className="os-window-sidebar" aria-label={t("deviceOs.sectionsAria")}>
                <span className="os-window-sidebar-title" aria-hidden="true">
                    {t("deviceOs.gate.sidebarTitle")}
                </span>
                {DEVICE_SECTIONS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className="os-window-side-item"
                        aria-current={item.id === sectionId ? "page" : undefined}
                        onClick={() => onSelect(item.id)}
                    >
                        {t(item.labelKey)}
                    </button>
                ))}
            </nav>

            <div className="os-window-main">
                <div className="os-window-toolbar">
                    <h2 id={headingId} className="os-window-title">
                        {t(section.labelKey)}
                    </h2>
                    <span className="os-window-url" aria-hidden="true">
                        {window.location.host}/{locale}#{section.id}
                    </span>
                </div>
                <div ref={bodyRef} className="os-window-body">
                    <SectionWindowContent id={section.id} onOpenFull={onOpenFull} />
                </div>
                <div className="os-window-footer">
                    <button type="button" className="os-window-full" onClick={onOpenFull}>
                        {t("deviceOs.gate.openFull")}
                        <FiArrowUpRight aria-hidden="true" />
                    </button>
                </div>
            </div>
        </motion.section>
    );
}
